import { useCallback, useState } from 'react';

/**
 * The original of one saved-Document export: the two client-minted ids that name its operation and
 * manifest. They are kept in this tab BEFORE the export is dispatched, so a reload or a remount
 * after a lost reply recovers THAT original by receipt lookup (Check again) instead of minting a
 * new one. They are lookup hints only: never authority, never proof that anything was exported or
 * is ready. No signed lease URL, file or payload is ever stored.
 */
export type ExportOriginal = { operationId: string; manifestId: string };
export type ExportOriginalScope = {
  companyId: string;
  actorId: string;
  artifactId: string;
  revisionId: string;
  versionId: string;
  number: number;
  purpose: string;
  format: string;
  locale: string;
  /** CSV selection belongs to the original; XLSX/Document keep their existing key. */
  sheetId?: string | null;
};

export const exportOriginalKey = (scope: ExportOriginalScope) =>
  [
    'poneva.export-original',
    scope.companyId,
    scope.actorId,
    scope.artifactId,
    scope.revisionId,
    scope.versionId,
    String(scope.number),
    scope.purpose,
    scope.format,
    scope.locale,
    ...(scope.sheetId != null ? [scope.sheetId] : []),
  ].join('\0');

export const mintExportOriginal = (): ExportOriginal => ({
  operationId: `export_${crypto.randomUUID()}`,
  manifestId: `files_${crypto.randomUUID()}`,
});

const stores = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** A stored original, only if it is exactly the two ids this module wrote. */
export function loadExportOriginal(
  scope: ExportOriginalScope,
  storage: Pick<Storage, 'getItem'> | null = stores(),
): ExportOriginal | null {
  try {
    const raw = storage?.getItem(exportOriginalKey(scope));
    if (!raw) return null;
    const value = JSON.parse(raw) as Record<string, unknown>;
    return Object.keys(value).sort().join() === 'manifestId,operationId' &&
      typeof value.operationId === 'string' &&
      /^export_[0-9a-f-]{36}$/u.test(value.operationId) &&
      typeof value.manifestId === 'string' &&
      /^files_[0-9a-f-]{36}$/u.test(value.manifestId)
      ? { operationId: value.operationId, manifestId: value.manifestId }
      : null;
  } catch {
    return null;
  }
}

export function retainExportOriginal(
  scope: ExportOriginalScope,
  original: ExportOriginal,
  storage: Pick<Storage, 'setItem'> | null = stores(),
) {
  try {
    storage?.setItem(
      exportOriginalKey(scope),
      JSON.stringify({ operationId: original.operationId, manifestId: original.manifestId }),
    );
  } catch {
    /* the in-memory original still holds for this mount */
  }
}

export function clearExportOriginal(
  scope: ExportOriginalScope,
  storage: Pick<Storage, 'removeItem'> | null = stores(),
) {
  try {
    storage?.removeItem(exportOriginalKey(scope));
  } catch {
    /* nothing to clear */
  }
}

/**
 * The export original for this person, company and exact saved tuple.
 * - `recovering`: a previous mount had already begun this original; the caller must then only look
 *   up its receipt (Check again), never export again.
 * - `begin()`: call right before an explicit export dispatch. It keeps the original (writing it
 *   before anything is sent) and returns the ids to send; after a resolved original it returns a
 *   FRESH one, so an explicit new export stays distinct from the one that finished.
 * - `resolved()`: the original reached a definite end (delivered); it is forgotten. The in-memory
 *   ids are left unchanged so nothing re-renders around a ready file.
 */
export function useExportOriginal(scope: ExportOriginalScope) {
  const key = exportOriginalKey(scope);
  const fresh = () => {
    const stored = loadExportOriginal(scope);
    return {
      key,
      original: stored ?? mintExportOriginal(),
      recovering: stored !== null,
      spent: false,
    };
  };
  const [state, setState] = useState(fresh);
  const current = state.key === key ? state : fresh();
  if (current !== state) setState(current);
  const begin = useCallback((): ExportOriginal => {
    const original = current.spent ? mintExportOriginal() : current.original;
    retainExportOriginal(scope, original);
    setState({ key, original, recovering: false, spent: false });
    return original;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, current.original, current.spent]);
  const resolved = useCallback(() => {
    clearExportOriginal(scope);
    setState((old) => ({ ...old, recovering: false, spent: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { original: current.original, recovering: current.recovering, begin, resolved };
}
